import { ICartViewModel, ItemAnalyticsModel, ItemListAnalyticsModel } from '@/@types/client-api';
import { AnalyticsEventName } from './constants';
import { datadogRum } from '@/lib/DatadogService';
import { delay } from '@/utils/promises';
import Cookies from 'js-cookie';
import { UAParser } from 'ua-parser-js';

export const mapCategories = (categories: string[]): Record<string, string> => {
  return categories.reduce(
    (acc, category, index) => {
      acc[`item_category${index === 0 ? '' : index + 1}`] = category;
      return acc;
    },
    {} as Record<string, string>
  );
};

export const ConvertItemAnalyticsModelBase = (
  itemAnalyticsModel?: ItemAnalyticsModel,
  itemListAnalyticsModel?: ItemListAnalyticsModel,
  itemIndex?: number
) => {
  if (!itemAnalyticsModel) return undefined;

  const normalizedIndex =
    typeof itemIndex === 'number' && itemIndex >= 0 ? Math.trunc(itemIndex) + 1 : 1;

  const price = itemAnalyticsModel?.price ?? 0;
  const listPrice = itemAnalyticsModel?.listPrice ?? 0;
  const explicitDiscount = itemAnalyticsModel?.discount ?? 0;
  const computedDiscount = Math.max(listPrice - price, 0);
  const discount = explicitDiscount > 0 ? explicitDiscount : computedDiscount;

  return {
    item_id: itemAnalyticsModel?.id,
    item_name: itemAnalyticsModel?.name,
    affiliation: itemAnalyticsModel?.affiliation,
    coupon: itemAnalyticsModel?.coupon,
    index: normalizedIndex,
    item_brand: itemAnalyticsModel?.brand,
    price,
    location_id: itemAnalyticsModel?.location,
    ...mapCategories(itemAnalyticsModel?.categories || []),
    parentId: itemAnalyticsModel?.parentId,
    parentName: itemAnalyticsModel?.parentName,
    discount,
    item_list_price: itemAnalyticsModel.listPrice,
    item_list_id: itemListAnalyticsModel?.listId,
    item_list_name: itemListAnalyticsModel?.listName
  };
};

export const ConvertCartToItemAnalyticsModels = (cart?: ICartViewModel) => {
  return (
    cart?.orderLines
      ?.filter(x => x !== undefined)
      .map((x, index) =>
        ConvertItemAnalyticsModelBase(x.itemAnalyticsModel, x.itemListAnalyticsModel, index)
      ) ?? []
  );
};

export const ConvertItemAnalyticsModel = (
  itemAnalyticsModel?: ItemAnalyticsModel,
  quantity?: number,
  itemListAnalyticsModel?: ItemListAnalyticsModel,
  itemIndex?: number
) => {
  return {
    currency: itemAnalyticsModel?.currency || undefined,
    value: itemAnalyticsModel?.price || 0,
    items: [
      quantity
        ? {
            ...ConvertItemAnalyticsModelBase(itemAnalyticsModel, itemListAnalyticsModel, itemIndex),
            quantity
          }
        : ConvertItemAnalyticsModelBase(itemAnalyticsModel, itemListAnalyticsModel, itemIndex)
    ]
  };
};

export const getUID = () =>
  String(Date.now().toString(32) + Math.random().toString(16)).replace(/\./g, '');

export const createTransactionId = (transactionPrefix: string) => transactionPrefix + getUID();

const getFirstVisit = () => {
  const existingCookie = Cookies.get('_pli_first_visit');
  if (existingCookie) return JSON.parse(existingCookie);

  const firstVisit = {
    date: new Date().toISOString().split('T')[0],
    timestamp: new Date().getTime()
  };
  Cookies.set('_pli_first_visit', JSON.stringify(firstVisit), { expires: 730 }); // 2 years
  return firstVisit;
};

const getTrafficSource = () => {
  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');
  const utmCampaign = params.get('utm_campaign');

  if (utmSource || utmMedium || utmCampaign) {
    return {
      source: utmSource || '',
      medium: utmMedium || '',
      campaign: utmCampaign || ''
    };
  }

  if (document.referrer) {
    try {
      const referrerHostname = new URL(document.referrer).hostname;
      if (referrerHostname !== window.location.hostname) {
        return {
          source: referrerHostname,
          medium: 'referral',
          campaign: ''
        };
      }
    } catch {
      return {
        source: '(direct)',
        medium: '(none)',
        campaign: ''
      };
    }
  }

  return {
    source: '(direct)',
    medium: '(none)',
    campaign: ''
  };
};

export const parseGACookie = (cookieValue: string | undefined) => {
  if (!cookieValue) {
    return { clientId: null, sessionNumber: null };
  }

  const parts = cookieValue.split('.');

  if (parts[0] === 'GS2' && parts.length >= 2) {
    const dataString = parts.slice(2).join('.');
    const sessionCountMatch = dataString.match(/\$o(\d+)/);
    const sessionIdMatch = dataString.match(/s(\d+)/);

    return {
      clientId: sessionIdMatch?.[1] || null,
      sessionNumber: sessionCountMatch?.[1] ? parseInt(sessionCountMatch[1], 10) : null
    };
  }

  if (parts[0] === 'GS1' && parts.length >= 4) {
    return {
      clientId: parts[2] || null,
      sessionNumber: parseInt(parts[3], 10) || null
    };
  }

  if (parts[0]?.startsWith('GA') && parts.length >= 3) {
    return {
      clientId: parts[2] || null,
      sessionNumber: null
    };
  }

  return { clientId: null, sessionNumber: null };
};

export const createSessionContextEvent = () => {
  const parser = new UAParser(navigator.userAgent);
  const result = parser.getResult();
  const trafficSource = getTrafficSource();
  const firstVisit = getFirstVisit();

  const measurementId = import.meta.env.VITE_APP_GA4_MEASUREMENT_ID;
  const cookieName = `_ga_${measurementId}`;
  const gaCookie = Cookies.get(cookieName) || Cookies.get('_ga');
  const { clientId, sessionNumber } = parseGACookie(gaCookie);

  return {
    client_id: clientId || 'unknown',
    event: 'session_context',
    ga_session_number: sessionNumber || '1',
    platform: result.os.name ? `${result.os.name} ${result.os.version || ''}`.trim() : 'unknown',
    os: result.os.name || 'unknown',
    browser: result.browser.name || 'unknown',
    device_category: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
    language: navigator.language,
    page_hostname: window.location.hostname,
    traffic_source: trafficSource,
    first_visit_date: firstVisit.date,
    first_visit_timestamp: firstVisit.timestamp.toString(),
    timestamp: new Date().getTime(),
    dataSource: 'FrontEnd'
  };
};

const eventListenersStack = new Array<() => void>();

export const PushAnalyticsEvent = (event: AnalyticsEventName, eventData: object) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null, items: null });

  const eventCallback = eventListenersStack.pop();
  if (eventCallback) Object.assign(eventData, { eventCallback });

  window.dataLayer.push({
    event: event,
    ...eventData
  });
};

/**
 * Wait for events to be tracked before performing actions (eg: redirect).
 *
 * ‼ Beware of using this too much, as it can create too many timeouts depending on the type of event
 * */
export const waitForEvent = async (event: () => unknown, timeout = 2000) => {
  const abort = new AbortController();

  try {
    return await Promise.race([
      new Promise<void>(resolve => {
        eventListenersStack.push(resolve);
        event();
      }),
      // timeout fallback
      delay(timeout, abort.signal)
    ]);
  } finally {
    abort.abort();
  }
};

const logError = (errorMessage: string) => {
  datadogRum.addError(new Error(errorMessage), {
    customMessage: errorMessage
  });
};

export const syntheticEventHubEvent = async (event: object) => {
  try {
    window.eventHubDataLayer = window.eventHubDataLayer || [];

    const analyticsEventTimestamp = new Date().getTime();
    const uniqueEventId = getUID(); // synthetic gtm.uniqueEventId
    const newEvent = {
      ...event,
      analytics_event_timestamp: analyticsEventTimestamp,
      'gtm.uniqueEventId': uniqueEventId
    };

    window.eventHubDataLayer.push(newEvent);
  } catch (error) {
    logError('synthetic hub event error');
  }
};
